import { useEffect, useState } from "react";

// Universeller Hook für Seiten/Tool-Settings (inkl. darkMode)
export function usePageSettings<T extends object>(
	cookieKey: string | undefined,
	defaultSettings: T,
) {
	// Lade Settings aus localStorage oder nutze Defaults
	const loadSettings = () => {
		if (!cookieKey) return defaultSettings;
		try {
			const stored = localStorage.getItem(cookieKey);
			if (stored) {
				const parsed = JSON.parse(stored);
				// Nur fehlende Werte aus defaultSettings ergänzen, nicht alles überschreiben!
				return { ...defaultSettings, ...parsed };
			}
		} catch (error) {
			console.error(`Failed to load settings for ${cookieKey}:`, error);
		}
		return defaultSettings;
	};

	const [settings, setSettings] = useState<T>(() => loadSettings());

	// Speichere Settings bei Änderung
	const updateSettings = (newSettings: Partial<T>) => {
		setSettings((prev) => {
			let current = prev;
			if (cookieKey) {
				try {
					const stored = localStorage.getItem(cookieKey);
					if (stored) {
						current = { ...defaultSettings, ...JSON.parse(stored) };
					}
				} catch (error) {
					console.error(`Failed to load settings for ${cookieKey}:`, error);
				}
			}
			const merged = { ...current, ...newSettings };
			if (cookieKey) {
				try {
					localStorage.setItem(cookieKey, JSON.stringify(merged));
				} catch (error) {
					console.error(`Failed to save settings for ${cookieKey}:`, error);
				}
			}
			return merged;
		});
	};

	return { settings, updateSettings };
}

// Globaler Dark Mode Hook (seitenübergreifend)
export function useDarkMode() {
	const DARK_MODE_KEY = "darkMode";

	// Cookie-Funktionen
	const setCookie = (name: string, value: string, days = 365) => {
		const expires = new Date();
		expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
		document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
	};

	const getCookie = (name: string): string | null => {
		const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
		return match ? match[2] : null;
	};

	// Lade Dark Mode Status aus Cookie und localStorage
	const loadDarkMode = (): boolean => {
		if (typeof window === "undefined") return true; // SSR Fallback

		try {
			// Prüfe zuerst Cookie
			const cookieValue = getCookie(DARK_MODE_KEY);
			if (cookieValue) {
				return cookieValue === "true";
			}

			// Dann localStorage
			const stored = localStorage.getItem(DARK_MODE_KEY);
			return stored ? JSON.parse(stored) : true; // Default: Dark Mode aktiviert
		} catch {
			return true;
		}
	};

	const [darkMode, setDarkMode] = useState<boolean>(() => loadDarkMode());

	// Dark Mode Status bei Änderung speichern und HTML-Klasse setzen
	useEffect(() => {
		if (typeof window === "undefined") return; // SSR Check

		try {
			// In localStorage speichern
			localStorage.setItem(DARK_MODE_KEY, JSON.stringify(darkMode));

			// Als Cookie setzen für seitenübergreifende Konsistenz
			setCookie(DARK_MODE_KEY, darkMode.toString());
		} catch (error) {
			console.error("Failed to save dark mode setting:", error);
		}

		// HTML-Klasse setzen für Tailwind Dark Mode
		if (darkMode) {
			document.documentElement.classList.add("dark");
		} else {
			document.documentElement.classList.remove("dark");
		}
	}, [darkMode]);

	const toggleDarkMode = () => setDarkMode(!darkMode);

	return { darkMode, toggleDarkMode };
}
